import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Bot, Mail, Phone, Send, X, ExternalLink } from 'lucide-react'

interface TelegramFooterProps {
  onCTAClick: () => void
}

export default function TelegramFooter({ onCTAClick }: TelegramFooterProps) {
  const currentYear = new Date().getFullYear()
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false)
  const [pendingUrl, setPendingUrl] = useState('')

  const handleExternalLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    e.preventDefault()
    setPendingUrl(url)
    setIsWarningModalOpen(true)
  }

  const handleContinue = () => {
    if (pendingUrl) {
      window.open(pendingUrl, '_blank', 'noopener,noreferrer')
    }
    setIsWarningModalOpen(false)
    setPendingUrl('')
  }

  const handleCancel = () => {
    setIsWarningModalOpen(false)
    setPendingUrl('')
  }

  const serviceLinks = [
    { name: 'WEB-сервисы', url: 'https://softrest.ru/web-service' },
    { name: 'Разработка API', url: 'https://softrest.ru/api' },
    { name: 'Разработка на Laravel', url: 'https://softrest.ru/laravel' },
    { name: 'Автоматизация', url: 'https://softrest.ru' }
  ]

  const companyLinks = [
    { name: 'О нас', url: 'https://softrest.ru' },
    { name: 'Портфолио', url: 'https://softrest.ru/cases' },
    { name: 'Блог', url: 'https://softrest.ru/blog' },
    { name: 'Контакты', url: 'https://softrest.ru/contacts' }
  ]

  return (
    <>
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Company info */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Bot className="w-8 h-8 text-blue-500" />
                <span className="text-2xl font-bold">SoftRest</span>
              </div>
              <p className="text-gray-400 mb-4">
                Разработка Telegram-ботов под ключ для автоматизации бизнеса
              </p>
              <a
                href="https://t.me/softrest_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
              >
                <Send className="w-4 h-4" />
                Открыть в Telegram
              </a>
            </div>

            {/* Services */}
            <div>
              <h3 className="font-bold text-lg mb-4">Услуги</h3>
              <ul className="space-y-2 text-gray-400">
                {serviceLinks.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.url}
                      onClick={(e) => handleExternalLinkClick(e, link.url)}
                      className="hover:text-white transition-colors cursor-pointer"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="font-bold text-lg mb-4">Компания</h3>
              <ul className="space-y-2 text-gray-400">
                {companyLinks.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.url}
                      onClick={(e) => handleExternalLinkClick(e, link.url)}
                      className="hover:text-white transition-colors cursor-pointer"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contacts */}
            <div>
              <h3 className="font-bold text-lg mb-4">Контакты</h3>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <a href="mailto:info@softrest.ru" className="hover:text-white transition-colors">
                    info@softrest.ru
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <a href="tel:+79291735565" className="hover:text-white transition-colors">
                    +7 (929) 173-55-65
                  </a>
                </li>
                <li>
                  <button
                    onClick={onCTAClick}
                    className="open-modal__btn px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors font-semibold text-white"
                  >
                    Оставить заявку
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-8 border-t border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">
                2025 ООО «Онего Веб-Софт»
              </p>
              <div className="flex gap-6 text-sm text-gray-400">
                <a 
                  href="https://softrest.ru/politics" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  Политика конфиденциальности
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Warning Modal */}
      <AnimatePresence>
        {isWarningModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCancel}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', duration: 0.3 }}
              className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8"
            >
              {/* Close button */}
              <button
                onClick={handleCancel}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <ExternalLink className="w-8 h-8 text-blue-600" />
                </div>
              </div>

              {/* Content */}
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Подтверждение перехода
                </h3>
                <p className="text-gray-600 text-lg">
                  Вы будете перенаправлены на старую версию сайта
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleCancel}
                  className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold transition-colors"
                >
                  Отмена
                </button>
                <button
                  onClick={handleContinue}
                  className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                >
                  Продолжить
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  )
}
